import CloseIcon from "@mui/icons-material/Close";
import Backdrop from "@mui/material/Backdrop";
import { useCallback, useEffect, useRef } from "react";
import { DEFAULT_LANDING_VIDEO } from "@/constants/campaignBuilder";
import {
  isDirectVideoFile,
  isYouTubeEmbedUrl,
  isYouTubeEndedMessage,
  toVideoEmbedUrl,
  YOUTUBE_ORIGIN,
} from "@/utils/video-helper";

const YOUTUBE_LISTENER_ID = "gift-video-modal";
// The player's message bridge may not be ready when the iframe loads, so the handshake is
// re-posted until the player answers (as YouTube's own widget API does), for a bounded time.
const YOUTUBE_HANDSHAKE_RETRY_MS = 250;
const YOUTUBE_HANDSHAKE_MAX_MS = 5000;

export interface GiftVideoModalProps {
  open: boolean;
  /** The landing gift-video: a pasted YouTube/Vimeo link (played in an <iframe> embed) OR a direct
   *  file URL (played in a native <video>). Empty falls back to DEFAULT_LANDING_VIDEO. The player
   *  mounts only while open so closing stops playback. */
  videoUrl?: string;
  onClose: () => void;
}

/**
 * Gift video-message modal — opened from the landing hero banner's play button.
 * Ported from the prototype's `.lp-media-modal`; z above the app but below the
 * confetti overlay is not required (it's a focused dialog).
 */
export default function GiftVideoModal({ open, videoUrl, onClose }: GiftVideoModalProps) {
  // The modal only opens when the landing "Video" toggle is on, so a campaign that enabled it
  // WITHOUT pasting a link still gets a video: fall back to the hosted default gift video (mirrors
  // RedemptionHero falling back to DEFAULT_LANDING_BANNER). Never an empty modal.
  const resolved = videoUrl?.trim() || DEFAULT_LANDING_VIDEO;
  // A direct file URL (the default R2 mp4, or any uploaded .mp4/.webm) plays in a native <video> —
  // an <iframe> gives no real player for a file. A YouTube/Vimeo watch URL renders the site's PAGE
  // in an iframe, so normalize those to the embed player instead.
  const directFile = isDirectVideoFile(resolved);
  const embedUrl = directFile ? undefined : toVideoEmbedUrl(resolved);
  // YouTube's end screen shows a grid of suggested videos (rel=0 only narrows it to the same
  // channel), so for a YouTube embed the modal closes itself when the video ends.
  const youTube = open && isYouTubeEmbedUrl(embedUrl);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Ask the player (enablejsapi=1) to post its state changes to this window.
  const subscribeToPlayerState = useCallback(() => {
    const target = iframeRef.current?.contentWindow;
    if (!target) return;
    target.postMessage(
      JSON.stringify({ event: "listening", id: YOUTUBE_LISTENER_ID, channel: "widget" }),
      YOUTUBE_ORIGIN,
    );
    target.postMessage(
      JSON.stringify({
        event: "command",
        func: "addEventListener",
        args: ["onStateChange"],
        id: YOUTUBE_LISTENER_ID,
        channel: "widget",
      }),
      YOUTUBE_ORIGIN,
    );
  }, []);

  // Latest-callback ref: parents pass inline arrows, and the handshake effect must not restart
  // (resetting the retry window, re-subscribing) on every parent re-render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!youTube) return;
    const retry = window.setInterval(subscribeToPlayerState, YOUTUBE_HANDSHAKE_RETRY_MS);
    const stopRetrying = () => window.clearInterval(retry);
    const giveUp = window.setTimeout(stopRetrying, YOUTUBE_HANDSHAKE_MAX_MS);
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== YOUTUBE_ORIGIN) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      // The player answered, so the subscription is live.
      stopRetrying();
      window.clearTimeout(giveUp);
      if (isYouTubeEndedMessage(event.data)) onCloseRef.current();
    };
    window.addEventListener("message", onMessage);
    return () => {
      stopRetrying();
      window.clearTimeout(giveUp);
      window.removeEventListener("message", onMessage);
    };
  }, [youTube, subscribeToPlayerState]);
  return (
    <>
      <Backdrop
        open={open}
        onClick={onClose}
        transitionDuration={0}
        // z above the builder's FullscreenPreviewOverlay (z-9999) so the video shows
        // when opened from the fullscreen landing preview; harmless elsewhere.
        sx={{ zIndex: 10000, background: "rgba(0,0,0,0.45)" }}
      />
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Gift video message"
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10001,
            width: "min(92vw, 960px)",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close video"
            // R03: invisible 44×44 hit area on phones via a ::before expanded 4px past the 36px circle.
            className="max-md:before:absolute max-md:before:-inset-1"
            style={{
              position: "absolute",
              top: -14,
              right: -14,
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "var(--color-white)",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 14px rgba(0,0,0,0.28)",
              color: "var(--color-rodeo-black)",
              zIndex: 3,
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
          <div
            style={{
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 16px 48px rgba(0,0,0,0.4)",
              lineHeight: 0,
              background: "var(--color-rodeo-black)",
            }}
          >
            {directFile ? (
              <video
                src={resolved}
                controls
                autoPlay
                playsInline
                style={{ display: "block", width: "100%", aspectRatio: "16 / 9", border: 0 }}
              >
                <track kind="captions" />
              </video>
            ) : (
              embedUrl && (
                <iframe
                  ref={iframeRef}
                  title="Gift video message"
                  src={embedUrl}
                  onLoad={youTube ? subscribeToPlayerState : undefined}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  style={{ display: "block", width: "100%", aspectRatio: "16 / 9", border: 0 }}
                />
              )
            )}
          </div>
        </div>
      )}
    </>
  );
}
